import {
  useServiceHistoryCheckQuery,
  useServiceHistoryCheckSaveMutation,
  useServiceHistoryTypesQuery
} from '../../api';
import styles from './styles.module.scss';
import type { ServiceHistoryCheckModel } from 'api-new/generated';
import { InlineSvg } from 'components/svg';
import React, { useEffect, useMemo, useState } from 'react';
import { Select } from 'shared/ui/controls';

type ServiceHistoryOption = { value: string; label: string };

const isValidServiceHistoryType = (t: { id?: number; name?: string | null }): t is { id: number; name: string } =>
  typeof t.id === 'number' && typeof t.name === 'string' && t.name.length > 0;

export type ServiceHistoryCheckProps = {
  regNo?: string;
  serviceHistoryCheck?: ServiceHistoryCheckModel | null;
  defaultValue?: number;
  onChange?: (serviceHistoryTypeId: number | null) => void;
  isDisabled?: boolean;
  isSolid?: boolean;
};

export const ServiceHistoryCheck: React.FC<ServiceHistoryCheckProps> = ({
  regNo,
  serviceHistoryCheck,
  defaultValue,
  onChange,
  isDisabled,
  isSolid
}) => {
  const { data: types, isLoading: isTypesLoading } = useServiceHistoryTypesQuery();
  const [shouldFetchLastCheck, setShouldFetchLastCheck] = useState(serviceHistoryCheck == null);
  const {
    data: fetchedLastCheck,
    isLoading: isCheckLoading,
    refetch: refetchLastCheck
  } = useServiceHistoryCheckQuery(regNo, {
    enabled: shouldFetchLastCheck
  });
  const lastCheck = shouldFetchLastCheck ? fetchedLastCheck ?? serviceHistoryCheck : serviceHistoryCheck;
  const saveMutation = useServiceHistoryCheckSaveMutation({
    onSuccess: async () => {
      setShouldFetchLastCheck(true);
      const result = await refetchLastCheck();
      if (result.data?.typeId) {
        setSelectedTypeId(result.data.typeId);
      }
      setIsEditing(false);
    }
  });

  const [isEditing, setIsEditing] = useState(false);
  const [selectedTypeId, setSelectedTypeId] = useState<number | null>(defaultValue ?? null);

  const hasCheck = !!lastCheck?.typeId;

  useEffect(() => {
    if (lastCheck?.typeId) {
      setSelectedTypeId(lastCheck.typeId);
    }
  }, [lastCheck?.typeId]);

  useEffect(() => {
    if (defaultValue !== undefined && !lastCheck?.typeId) {
      setSelectedTypeId(defaultValue);
    }
  }, [defaultValue, lastCheck]);

  const options: ServiceHistoryOption[] = useMemo(
    () => (types ?? []).filter(isValidServiceHistoryType).map((t) => ({ value: t.id.toString(), label: t.name })),
    [types]
  );

  const selectedOption = useMemo(
    () => options.find((o) => o.value === selectedTypeId?.toString()) ?? null,
    [options, selectedTypeId]
  );

  const handleChange = (option: ServiceHistoryOption | null) => {
    const typeId = option?.value ? parseInt(option.value, 10) : null;
    setSelectedTypeId(typeId);
    onChange?.(typeId);
  };

  const handleToggleEdit = () => {
    setIsEditing(true);
  };

  const handleConfirm = () => {
    if (!regNo || !selectedTypeId) return;
    saveMutation.mutate({ regNo, typeId: selectedTypeId });
  };

  const isLoading = isTypesLoading || (shouldFetchLastCheck && isCheckLoading);

  return (
    <div>
      <div className="d-flex align-items-center mb-2">
        {hasCheck && (
          <span className="me-2">
            <InlineSvg path="/media/icons/custom/check-circle-2.svg" className="svg-icon-2" />
          </span>
        )}
        <span className="fw-bold fs-6 text-gray-800">Service History</span>
        {hasCheck && !isEditing && !isDisabled && (
          <button onClick={handleToggleEdit} className={styles.editableButton}>
            <InlineSvg path="/media/icons/custom/pencil.svg " />
          </button>
        )}
      </div>

      <Select
        value={selectedOption}
        onChange={handleChange}
        options={options}
        isLoading={isLoading}
        isDisabled={isDisabled || (hasCheck && !isEditing)}
        isSearchable={false}
        placeholder="Select service history type"
        controlMinHeight={40}
        isSolid={isSolid}
      />

      {hasCheck && lastCheck?.dateChecked && (
        <div className="text-muted fs-8">
          {new Date(lastCheck.dateChecked).toLocaleDateString('en-GB')} by {lastCheck.userNameChecked}
        </div>
      )}

      {(!hasCheck || isEditing) && (
        <button
          className="btn btn-sm btn-primary mt-2"
          onClick={handleConfirm}
          disabled={!selectedTypeId || saveMutation.isLoading || !regNo}
        >
          Confirm
        </button>
      )}
    </div>
  );
};
